import { useEffect, useRef, useState } from "react";
import {
  Eye,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Trash2,
} from "lucide-react";
import type {
  CalidadIncidencia,
  CalidadIncidenciasFilters,
} from "../types/calidad";
import {
  formatCalidadDate,
  hasActiveCalidadFilters,
} from "../types/calidad";

type Props = {
  incidencias: CalidadIncidencia[];
  total: number;
  currentPage: number;
  lastPage: number;
  from: number | null;
  to: number | null;
  loading: boolean;
  error: string | null;
  filters: CalidadIncidenciasFilters;
  onFiltersChange: (patch: Partial<CalidadIncidenciasFilters>) => void;
  onRetry: () => void;
  onResetFilters: () => void;
  onPageChange: (page: number) => void;
  onCreate: () => void;
  onEdit: (incidencia: CalidadIncidencia) => void;
  onDelete: (incidencia: CalidadIncidencia) => void;
  onOpenFicha: (incidencia: CalidadIncidencia) => void;
};

export function CalidadIncidenciasTable({
  incidencias,
  total,
  currentPage,
  lastPage,
  from,
  to,
  loading,
  error,
  filters,
  onFiltersChange,
  onRetry,
  onResetFilters,
  onPageChange,
  onCreate,
  onEdit,
  onDelete,
  onOpenFicha,
}: Props) {
  const hasFilters = hasActiveCalidadFilters(filters);
  const topScrollRef = useRef<HTMLDivElement>(null);
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const tableWrapRef = useRef<HTMLDivElement>(null);
  const syncingScrollRef = useRef(false);
  const [scrollWidth, setScrollWidth] = useState(0);
  const [hasHorizontalOverflow, setHasHorizontalOverflow] = useState(false);

  useEffect(() => {
    const updateDimensions = () => {
      const scroll = tableScrollRef.current;
      const wrap = tableWrapRef.current;
      if (!scroll || !wrap) return;

      setScrollWidth(wrap.scrollWidth);
      setHasHorizontalOverflow(wrap.scrollWidth > scroll.clientWidth + 1);
    };

    updateDimensions();
    const observer = new ResizeObserver(updateDimensions);
    if (tableScrollRef.current) observer.observe(tableScrollRef.current);
    if (tableWrapRef.current) observer.observe(tableWrapRef.current);
    window.addEventListener("resize", updateDimensions);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateDimensions);
    };
  }, [incidencias.length, loading]);

  const syncHorizontalScroll = (
    source: HTMLDivElement | null,
    target: HTMLDivElement | null,
  ) => {
    if (!source || !target || syncingScrollRef.current) return;
    syncingScrollRef.current = true;
    target.scrollLeft = source.scrollLeft;
    window.requestAnimationFrame(() => {
      syncingScrollRef.current = false;
    });
  };

  return (
    <section className="calidad-panel">
      <div className="calidad-toolbar">
        <div>
          <div className="calidad-toolbar-eyebrow">Registro importado</div>
          <h2 className="calidad-toolbar-title">Incidencias de calidad</h2>
          <p className="calidad-toolbar-copy">
            Consulta el histórico disponible, gestiona el CRUD del registro y
            accede a la ficha NCp cuando proceda.
          </p>
        </div>

        <div className="calidad-toolbar-meta">
          <span className="calidad-toolbar-count">
            {loading ? "Cargando..." : `${total} registros`}
          </span>

          {hasFilters && (
            <button
              type="button"
              onClick={onResetFilters}
              className="calidad-toolbar-reset"
            >
              Limpiar filtros
            </button>
          )}

          <button
            type="button"
            onClick={onCreate}
            className="calidad-primary-button"
          >
            <Plus size={16} />
            Nueva incidencia
          </button>
        </div>
      </div>

      <div className="calidad-filters">
        <label className="calidad-number-field">
          <span>Nº</span>
          <input
            type="text"
            inputMode="numeric"
            value={filters.numero}
            onChange={(event) =>
              onFiltersChange({
                numero: event.target.value.replace(/[^\d]/g, ""),
              })
            }
            placeholder="Filtrar por Nº"
          />
        </label>

        <label className="calidad-search-field">
          <Search size={16} />
          <input
            type="search"
            value={filters.search}
            onChange={(event) => onFiltersChange({ search: event.target.value })}
            placeholder="Buscar en literal o comentarios"
          />
        </label>

        <select
          value={filters.tipo_origen}
          onChange={(event) =>
            onFiltersChange({
              tipo_origen: event.target.value as CalidadIncidenciasFilters["tipo_origen"],
            })
          }
          className="calidad-select"
        >
          <option value="">Origen: todos</option>
          <option value="q">Q</option>
          <option value="ma">MA</option>
        </select>

        <select
          value={filters.tipo}
          onChange={(event) =>
            onFiltersChange({
              tipo: event.target.value as CalidadIncidenciasFilters["tipo"],
            })
          }
          className="calidad-select"
        >
          <option value="">Tipo: todos</option>
          <option value="ncp">NCp</option>
          <option value="df">Df</option>
        </select>
      </div>

      {error ? (
        <div className="calidad-feedback-state">
          <div className="calidad-feedback-title">No se pudo cargar el listado</div>
          <p className="calidad-feedback-copy">{error}</p>
          <button
            type="button"
            onClick={onRetry}
            className="calidad-feedback-action"
          >
            <RefreshCw size={15} />
            Reintentar
          </button>
        </div>
      ) : loading ? (
        <div className="calidad-feedback-state">
          <Loader2 size={20} className="animate-spin text-[#0b4a2f]" />
          <div className="calidad-feedback-title">Cargando incidencias</div>
          <p className="calidad-feedback-copy">
            Estamos recuperando el registro histórico de calidad.
          </p>
        </div>
      ) : incidencias.length === 0 ? (
        <div className="calidad-feedback-state">
          <div className="calidad-feedback-title">No hay incidencias para mostrar</div>
          <p className="calidad-feedback-copy">
            Ajusta los filtros o crea una nueva incidencia para iniciar el
            registro operativo.
          </p>
          <button
            type="button"
            onClick={onCreate}
            className="calidad-primary-button"
          >
            <Plus size={16} />
            Nueva incidencia
          </button>
        </div>
      ) : (
        <>
          <div
            ref={topScrollRef}
            className={`calidad-table-top-scroll${hasHorizontalOverflow ? " is-visible" : ""}`}
            aria-hidden={!hasHorizontalOverflow}
            onScroll={() => syncHorizontalScroll(topScrollRef.current, tableScrollRef.current)}
          >
            <div style={{ width: `${scrollWidth}px` }} />
          </div>
          <div
            ref={tableScrollRef}
            className="calidad-table-scroll"
            onScroll={() => syncHorizontalScroll(tableScrollRef.current, topScrollRef.current)}
          >
            <div ref={tableWrapRef} className="calidad-table-wrap">
              <table className="calidad-table min-w-full">
                <thead>
                  <tr>
                    <th>Acciones</th>
                    <th>Nº</th>
                    <th>Fecha</th>
                    <th>Q</th>
                    <th>MA</th>
                    <th>Literal</th>
                    <th>NCp</th>
                    <th>Df</th>
                    <th>Comentarios para cierre</th>
                    <th>Comentarios cliente / auditor</th>
                  </tr>
                </thead>

                <tbody>
                  {incidencias.map((incidencia) => (
                    <tr key={incidencia.id}>
                      <td>
                        <div className="calidad-table-actions-row">
                          <button
                            type="button"
                            onClick={() => onOpenFicha(incidencia)}
                            disabled={!incidencia.ncp}
                            title={
                              incidencia.ncp
                                ? "Abrir ficha NCp"
                                : "La ficha solo está disponible para incidencias NCp"
                            }
                            aria-label="Abrir ficha"
                            className="calidad-icon-button"
                          >
                            <Eye size={15} />
                          </button>

                          <button
                            type="button"
                            onClick={() => onEdit(incidencia)}
                            title="Editar incidencia"
                            aria-label="Editar incidencia"
                            className="calidad-icon-button"
                          >
                            <Pencil size={15} />
                          </button>

                          <button
                            type="button"
                            onClick={() => onDelete(incidencia)}
                            title="Eliminar incidencia"
                            aria-label="Eliminar incidencia"
                            className="calidad-icon-button danger"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                      <td>{incidencia.numero}</td>
                      <td>{formatCalidadDate(incidencia.fecha)}</td>
                      <td>
                        <BooleanCell checked={incidencia.q} label="Q" />
                      </td>
                      <td>
                        <BooleanCell checked={incidencia.ma} label="MA" />
                      </td>
                      <td>
                        <div className="calidad-literal-cell">
                          {incidencia.literal?.trim() || "Sin descripción"}
                        </div>
                      </td>
                      <td>
                        <BooleanCell checked={incidencia.ncp} label="NCp" />
                      </td>
                      <td>
                        <BooleanCell checked={incidencia.df} label="Df" />
                      </td>
                      <td>{renderOptionalText(incidencia.comentarios_para_cierre)}</td>
                      <td>{renderOptionalText(incidencia.comentarios_cliente_auditor)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="calidad-pagination">
            <div className="calidad-pagination-meta">
              {from !== null && to !== null
                ? `Mostrando ${from}-${to} de ${total}`
                : `${total} registros`}
            </div>

            <div className="calidad-pagination-controls">
              <button
                type="button"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage <= 1}
                className="calidad-pagination-button"
              >
                Anterior
              </button>

              <span className="calidad-pagination-page">
                {`Página ${currentPage} de ${Math.max(1, lastPage)}`}
              </span>

              <button
                type="button"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage >= lastPage}
                className="calidad-pagination-button"
              >
                Siguiente
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

function BooleanCell({
  checked,
  label,
}: {
  checked: boolean;
  label: string;
}) {
  return (
    <span className="calidad-boolean-cell">
      <input
        type="checkbox"
        checked={checked}
        readOnly
        disabled
        aria-label={label}
        className="calidad-checkbox"
      />
    </span>
  );
}

function renderOptionalText(value: string | null) {
  if (!value?.trim()) {
    return <span className="calidad-muted-copy">Sin comentarios</span>;
  }

  return value;
}
